/**@flow */

import React, { Fragment } from "react";
import { localize } from "redux-i18n";
import config from "@config";
import { formatDateTime } from "@transformers/date";
import {
  Button,
  Icons,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  GSGrid,
  SectionComponent,
} from "@components";
import { AddMedicationModal } from "@modules/employee/components";
import { connect } from "react-redux";
import * as employeeSelectors from "@modules/employee/selectors";
import actionCreator from "@modules/employee/actionCreator";
import { toTableSort } from "@transformers/sort";
import type { Medications as MedicationsType } from "../../reducer/medical-details/types";
import type { PaginationExtendedType, SortOrder } from "@types";
import type { State } from "@store/reducers";

type PropsFromDispatch = $Exact<{
  onDismissAddMedicationHandler: typeof actionCreator.ToggleAddMedicationModal,
  onAddMedicationHandler: typeof actionCreator.ToggleAddMedicationModal,
  toggleRemoveMedicationModal: typeof actionCreator.ToggleRemoveMedicationModal,
  onDismissRemoveMedicationHandler: typeof actionCreator.ToggleRemoveMedicationModal,
  addMedication: typeof actionCreator.AddMedication,
  updateMedication: typeof actionCreator.UpdateMedication,
  removeMedication: typeof actionCreator.RemoveMedication,
  onSort: typeof actionCreator.SetEmployeeMedicationSort,
}>;
type PropsFromState = $Exact<{
  medicationId: number,
  schema: Object,
  isAddMedicationModalVisible: boolean,
  isRemoveMedicationModalVisible: boolean,
  medicationName: string,
  pagination: PaginationExtendedType,
  sort: Array<SortOrder> | null,
}>;
type Props = $Exact<{
  ...PropsFromLocalize,
  ...PropsFromDispatch,
  ...PropsFromState,
  medications: MedicationsType[],
  employeeId: number,
  clientId: number,
  onPageChange: (page: number) => void,
}>;

export class Medications extends React.Component<Props> {
  removeMedication = () =>
    this.props.removeMedication({
      medicationId: this.props.medicationId,
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });

  onAddMedication = ({ formData }: Object) => {
    this.props.addMedication({
      formData,
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });
  };

  onToggleRemoveMedicationModal = (id: number) => () =>
    this.props.toggleRemoveMedicationModal({
      medicationId: id,
      visible: true,
    });

  onSort = (sort: $Exact<{ id: string | null, direction: "asc" | "desc" }>) => {
    this.props.onSort({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      sort,
    });
  };

  handleChangeStatus = (status: string, medication: MedicationsType) => () => {
    this.props.updateMedication({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      data: {
        status,
        id: medication.id,
        medication: medication.medication,
        notes: medication.notes,
      },
      medicationDetailsId: medication.id,
    });
  };

  get columns() {
    return [
      {
        title: this.props.t("ID"),
        renderCell: ({ row }: { row: Object }) => row.medication.id,
        name: "id",
        sortable: true,
      },
      {
        title: this.props.t("MEDICATION"),
        renderCell: ({ row }: { row: Object }) => row.medication.name,
        name: "medication",
        sortable: true,
      },
      {
        title: this.props.t("MEDICAL_DETAILS_TABLE_TITLE_NOTES"),
        renderCell: ({ row }: { row: Object }) => row.notes,
        name: "notes",
      },
      {
        title: this.props.t("MEDICAL_DETAILS_TABLE_TITLE_STATUS"),
        renderCell: ({ row }: { row: Object }) => this.props.t(row.status),
        name: "status",
      },
      {
        title: this.props.t("DATE_RECORDED"),
        renderCell: ({ row }: { row: Object }) => formatDateTime(row.createdAt),
        name: "createdAt",
        sortable: true,
      },
      {
        title: this.props.t("RECORDED_BY"),
        renderCell: ({ row }: { row: Object }) =>
          `${row.updatedBy.firstName} ${row.updatedBy.lastName}`,
        name: "recorderBy",
      },
      {
        title: this.props.t("ASSOCIATED_ABSENCE"),
        name: "notAssociatedAbsence",
        renderCell: ({ row }: { row: Object }) =>
          row.absenceId || this.props.t("NOT_ASSOCIATED_ABSENCE"),
      },
      {
        title: "",
        name: "update",
        renderCell: ({ row }: { row: MedicationsType }) => {
          if (row.status === "ACTIVE") {
            return (
              <Button
                size="small"
                className="text-nowrap"
                onClick={this.handleChangeStatus("INACTIVE", row)}
              >
                {this.props.t("INACTIVE")}
              </Button>
            );
          }
          return (
            <Button
              size="small"
              className="text-nowrap"
              onClick={this.handleChangeStatus("ACTIVE", row)}
            >
              {this.props.t("ACTIVE")}
            </Button>
          );
        },
      },
      {
        title: "",
        name: "actions",
        renderCell: ({ row }: { row: MedicationsType }) => {
          return (
            <span
              className="material-icons font-size-26 pointer"
              onClick={this.onToggleRemoveMedicationModal(row.id)}
            >
              delete
            </span>
          );
        },
      },
    ];
  }

  render() {
    return (
      <Fragment>
        <SectionComponent
          title={<h5>{this.props.t("MEDICATIONS")}</h5>}
          wrapperClassName="flex-column"
          rightLink={
            <Button materialIcon="add_box" onClick={this.props.onAddMedicationHandler}>
              {this.props.t("ADD")}
            </Button>
          }
        >
          <GSGrid
            className="pb-5"
            rows={this.props.medications}
            columns={this.columns}
            onSortChange={this.onSort}
            sort={toTableSort(this.props.sort)}
          />
          <Pagination
            className="my-3 d-flex justify-content-center"
            currentPage={this.props.pagination.pageNumber}
            totalPages={this.props.pagination.totalPages}
            onChange={this.props.onPageChange}
          />
        </SectionComponent>
        {this.props.schema && this.props.isAddMedicationModalVisible && (
          <AddMedicationModal
            onDismissHandler={this.props.onDismissAddMedicationHandler}
            onSubmit={this.onAddMedication}
            schema={this.props.schema}
          />
        )}
        {this.props.isRemoveMedicationModalVisible && (
          <Modal className="modal-small">
            <ModalHeader toggle={this.props.onDismissRemoveMedicationHandler}>
              {this.props.t("ARE_YOU_SURE")}
            </ModalHeader>
            <ModalBody>
              {this.props.t("REMOVE_CONFIRMATION", {
                item: <strong>{this.props.medicationName}</strong>,
              })}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={this.props.onDismissRemoveMedicationHandler}>
                {this.props.t("CANCEL")}
              </Button>
              <Button onClick={this.removeMedication}>{this.props.t("REMOVE_MEDICATION")}</Button>
            </ModalFooter>
          </Modal>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  const medicationId = employeeSelectors.getMedicationId(state);

  return {
    isAddMedicationModalVisible: employeeSelectors.getIsAddMedicationModalVisible(state),
    isRemoveMedicationModalVisible: employeeSelectors.getIsRemoveMedicationModalVisible(state),
    schema: employeeSelectors.getMedicationSchema(state),
    medicationId,
    medicationName: employeeSelectors.getMedicationItemName(medicationId)(state),
    pagination: employeeSelectors.getMedicationsPagination(state),
    sort: employeeSelectors.getEmployeeMedicationSort(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  onDismissAddMedicationHandler: () => actionCreator.ToggleAddMedicationModal(false),
  onAddMedicationHandler: () => actionCreator.ToggleAddMedicationModal(true),
  toggleRemoveMedicationModal: actionCreator.ToggleRemoveMedicationModal,
  onDismissRemoveMedicationHandler: () =>
    actionCreator.ToggleRemoveMedicationModal({ visible: false }),
  addMedication: actionCreator.AddMedication,
  updateMedication: actionCreator.UpdateMedication,
  removeMedication: actionCreator.RemoveMedication,
  onSort: actionCreator.SetEmployeeMedicationSort,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(Medications));
